// src/pages/Dashboard.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1 (debounce 5s + silent
// refresh para no inundar el backend con 1500 clientes).
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import api from '../services/api';
import socket from '../services/socket';
import DashboardCalendar from '../components/DashboardCalendar';


// ── Iconos SVG inline ────────────────────────────────────────────────────────
const Icon = ({ children, size = 18, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    inbox:    <Icon><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" /></Icon>,
    inProgress: <Icon><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
    check:    <Icon><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></Icon>,
    alert:    <Icon><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></Icon>,
    clock:    <Icon><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
    star:     <Icon><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z" /></Icon>,
    layers:   <Icon><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></Icon>,
    plus:     <Icon><path d="M12 5v14" /><path d="M5 12h14" /></Icon>,
    building: <Icon><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" /></Icon>,
    users:    <Icon><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>,
    logout:   <Icon><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></Icon>,
    globe:    <Icon size={14}><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></Icon>,
};


// ── Tokens semánticos ────────────────────────────────────────────────────────
const STATUS_COLOR = { open: '#2563eb', in_progress: '#d97706', closed: '#059669' };
const STATUS_LABEL = { open: 'Abierto', in_progress: 'En progreso', closed: 'Resuelto' };
const PRIO_COLOR   = { low: '#059669', medium: '#2563eb', high: '#d97706', urgent: '#dc2626' };
const PRIO_LABEL   = { low: 'Baja', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
const BRAND_BLUE   = '#1d4ed8';


// ── Scoped CSS ───────────────────────────────────────────────────────────────
const DASH_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

.dash-root {
    --dash-accent: var(--brand-700, #0a4aa8);
    --dash-accent-hover: var(--brand-800, #07357f);
    --dash-accent-light: #dbeafe;
    --dash-accent-ring: rgba(29, 78, 216, .12);
    --dash-radius-lg: 14px;
    --dash-radius-md: 10px;
    --dash-radius-sm: 8px;
    --dash-shadow-sm: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    --dash-shadow-hover: 0 4px 16px rgba(15,23,42,.06), 0 2px 4px rgba(15,23,42,.04);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    background: var(--c-bg);
    min-height: 100vh;
    padding: 24px 24px 60px;
    color: var(--c-text-1);
}
.dash-root * { box-sizing: border-box; }
.dash-root h1 { letter-spacing: -0.02em; margin: 0; }

.dash-container { max-width: 1280px; margin: 0 auto; }
.dash-container-narrow { max-width: 820px; margin: 0 auto; }

/* Header */
.dash-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 24px; flex-wrap: wrap; gap: 16px;
}
.dash-title { font-size: 26px; font-weight: 700; color: var(--c-text-1); margin: 0 0 4px; }
.dash-subtitle { color: var(--c-text-3); font-size: 14px; margin: 0; }

.dash-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

/* Select estilo "filter" */
.dash-select {
    padding: 7px 11px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: var(--dash-radius-sm);
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none; min-width: 160px;
    transition: border-color .15s, box-shadow .15s;
}
.dash-select:focus { border-color: var(--dash-accent); box-shadow: 0 0 0 3px var(--dash-accent-ring); }

/* Botones */
.dash-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent;
    border-radius: var(--dash-radius-sm);
    padding: 7px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all .15s ease;
    text-decoration: none; font-family: inherit;
}
.dash-btn-primary { background: var(--dash-accent); color: #fff; border-color: var(--dash-accent); box-shadow: 0 1px 3px rgba(29,78,216,.25); }
.dash-btn-primary:hover { background: var(--dash-accent-hover); border-color: var(--dash-accent-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(29,78,216,.25); }
.dash-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.dash-btn-ghost:hover { background: var(--c-msg-bg); border-color: var(--c-text-3); }
.dash-btn-danger-ghost { background: transparent; color: #dc2626; border-color: #fecaca; }
.dash-btn-danger-ghost:hover { background: var(--c-priority-urgent-bg); border-color: #dc2626; }

/* KPI cards */
.dash-kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 14px; margin-bottom: 24px;
}
.dash-kpi-grid.cols-3 { grid-template-columns: repeat(3, 1fr); }
.dash-kpi {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: var(--dash-radius-lg);
    box-shadow: var(--dash-shadow-sm);
    padding: 18px 20px;
    position: relative; overflow: hidden;
    transition: transform .18s, box-shadow .18s;
}
.dash-kpi:hover { transform: translateY(-2px); box-shadow: var(--dash-shadow-hover); }
.dash-kpi::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px; background: var(--kpi-accent, var(--dash-accent));
}
.dash-kpi-head {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    margin-bottom: 10px;
}
.dash-kpi-label {
    font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase; color: var(--c-text-4); margin: 0;
}
.dash-kpi-icon {
    color: var(--kpi-accent, var(--dash-accent));
    display: inline-flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border-radius: 8px;
    background: var(--kpi-accent-bg, var(--dash-accent-light));
}
.dash-kpi-value {
    font-size: 30px; font-weight: 800; color: var(--c-text-1);
    margin: 0; line-height: 1; letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
}
.dash-kpi-sub { font-size: 11px; color: var(--c-text-4); margin: 6px 0 0; }

/* Chart cards */
.dash-charts-row {
    display: grid; gap: 14px; margin-bottom: 14px;
}
.dash-charts-row.cols-2-3-5 { grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); }
.dash-charts-row.cols-1-1   { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
.dash-charts-row.full       { grid-template-columns: 1fr; }
@media (max-width: 920px) {
    .dash-charts-row.cols-2-3-5, .dash-charts-row.cols-1-1 { grid-template-columns: 1fr; }
}
.dash-chart-card {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: var(--dash-radius-lg);
    box-shadow: var(--dash-shadow-sm);
    overflow: hidden;
}
.dash-chart-head {
    padding: 14px 20px;
    border-bottom: 1px solid var(--c-card-header-border);
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.dash-chart-title {
    font-size: 13px; font-weight: 600; color: var(--c-text-2); margin: 0;
    display: inline-flex; align-items: center; gap: 8px;
    letter-spacing: 0.1px;
}
.dash-chart-body { padding: 16px 8px 8px; }

/* Alert (overdue para user) */
.dash-alert {
    background: #fffbeb; border: 1px solid #fde68a;
    border-radius: var(--dash-radius-md); padding: 14px 18px;
    color: #92400e; font-size: 14px; margin-bottom: 24px;
    display: flex; align-items: center; gap: 10px;
}
.dash-alert a { color: #92400e; font-weight: 700; text-decoration: underline; }
[data-bs-theme="dark"] .dash-alert {
    background: rgba(245, 158, 11, .1); border-color: rgba(245, 158, 11, .3); color: #fbbf24;
}
[data-bs-theme="dark"] .dash-alert a { color: #fbbf24; }

.dash-footer-meta {
    text-align: right; color: var(--c-text-4); font-size: 11px;
    margin-top: 12px;
    display: flex; align-items: center; justify-content: flex-end; gap: 6px;
}
.dash-loading { text-align: center; padding: 80px 0; }
.dash-pulse {
    display: inline-block; width: 7px; height: 7px; border-radius: 50%;
    background: #10b981; margin-right: 4px;
    animation: dash-pulse 1.6s ease-in-out infinite;
}
@keyframes dash-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.8); }
}

@keyframes dash-fade {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
}
.dash-fade { animation: dash-fade .3s ease-out both; }

@media (max-width: 600px) {
    .dash-root { padding: 16px 14px 60px; }
    .dash-kpi { padding: 14px 16px; }
    .dash-kpi-value { font-size: 26px; }
    /* 3 KPIs fijos no caben en 390px: a 1 columna */
    .dash-kpi-grid.cols-3 { grid-template-columns: 1fr; }
    /* 16px evita el zoom de iOS al enfocar */
    .dash-select { font-size: 16px; }
}
`;

// Ancho del eje de categorías en gráficas horizontales: en teléfono 130px
// dejaban ~200px para las barras.
const useNarrow = () => {
    const [narrow, setNarrow] = React.useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);
    React.useEffect(() => {
        const mq = window.matchMedia('(max-width: 600px)');
        const on = (e) => setNarrow(e.matches);
        mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
        return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
    }, []);
    return narrow;
};


// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ title, value, color, accentBg, icon, subtitle }) {
    return (
        <div className="dash-kpi" style={{ '--kpi-accent': color, '--kpi-accent-bg': accentBg }}>
            <div className="dash-kpi-head">
                <p className="dash-kpi-label">{title}</p>
                {icon && <span className="dash-kpi-icon">{icon}</span>}
            </div>
            <p className="dash-kpi-value">{value}</p>
            {subtitle && <p className="dash-kpi-sub">{subtitle}</p>}
        </div>
    );
}

// ── Chart Card ───────────────────────────────────────────────────────────────
function ChartCard({ title, icon, children, height = 240, action }) {
    return (
        <div className="dash-chart-card">
            <div className="dash-chart-head">
                <p className="dash-chart-title">{icon}{title}</p>
                {action}
            </div>
            <div className="dash-chart-body">
                <ResponsiveContainer width="100%" height={height}>
                    {children}
                </ResponsiveContainer>
            </div>
        </div>
    );
}


// ── Helpers ──────────────────────────────────────────────────────────────────
function buildStatusData(byStatus) {
    return ['open', 'in_progress', 'closed'].map(s => ({
        name: STATUS_LABEL[s] || s,
        count: byStatus.find(x => x.status === s)?.count || 0,
        fill: STATUS_COLOR[s],
    }));
}
function buildPriorityData(byPriority) {
    return ['low', 'medium', 'high', 'urgent'].map(p => ({
        name: PRIO_LABEL[p] || p,
        value: byPriority.find(x => x.priority === p)?.count || 0,
        fill: PRIO_COLOR[p],
    }));
}
function fmtDate(iso) { const [, m, d] = iso.split('-'); return `${d}/${m}`; }
function fmtCsat(val) { return val != null ? val.toFixed(1) + ' ★' : '—'; }
function fmtHours(h) {
    if (!h || h <= 0) return '—';
    if (h < 1)  return '< 1 h';
    if (h < 24) return `${Math.round(h)} h`;
    return `${Math.round(h / 24)} días`;
}


// ── Sección de gráficas compartida ──────────────────────────────────────────
function StatsCharts({ stats }) {
    const narrow = useNarrow();
    const catAxisW = narrow ? 88 : 130; // eje de nombres en gráficas horizontales
    const statusData   = buildStatusData(stats.byStatus);
    const priorityData = buildPriorityData(stats.byPriority);
    const deptData     = (stats.byDepartment || []).slice(0, 8);
    const agentData    = (stats.byAgent || []).slice(0, 8);
    const trendData    = (stats.last7days || []).map(d => ({ ...d, fecha: fmtDate(d.date) }));

    return (
        <>
            <div className="dash-charts-row cols-2-3-5">
                <ChartCard title="Tickets por estado">
                    <BarChart data={statusData} barSize={48}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--c-border)" />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(29, 78, 216, .06)' }} />
                        <Bar dataKey="count" name="Tickets" radius={[6, 6, 0, 0]}>
                            {statusData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                        </Bar>
                    </BarChart>
                </ChartCard>
                <ChartCard title="Tickets por prioridad">
                    <PieChart>
                        <Pie data={priorityData} dataKey="value" nameKey="name"
                            innerRadius={55} outerRadius={92} paddingAngle={3}
                            stroke="var(--c-card)" strokeWidth={2}>
                            {priorityData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    </PieChart>
                </ChartCard>
            </div>

            <div className="dash-charts-row cols-1-1">
                <ChartCard title="Tickets por departamento">
                    <BarChart data={deptData} layout="vertical" barSize={18}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--c-border)" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                        <YAxis type="category" dataKey="name" width={catAxisW} tick={{ fontSize: narrow ? 10 : 11, fill: 'var(--c-text-2)' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(29, 78, 216, .06)' }} />
                        <Bar dataKey="count" name="Tickets" fill={BRAND_BLUE} radius={[0, 6, 6, 0]} />
                    </BarChart>
                </ChartCard>
                <ChartCard title="Nuevos tickets — últimos 7 días">
                    <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--c-border)" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Line type="monotone" dataKey="count" name="Tickets"
                            stroke={BRAND_BLUE} strokeWidth={2.5}
                            dot={{ r: 4, fill: BRAND_BLUE, strokeWidth: 0 }}
                            activeDot={{ r: 6, fill: BRAND_BLUE, strokeWidth: 2, stroke: 'var(--c-card)' }} />
                    </LineChart>
                </ChartCard>
            </div>

            {agentData.length > 0 && (
                <div className="dash-charts-row full">
                    <ChartCard title="Tickets por agente" height={Math.max(180, agentData.length * 36)}>
                        <BarChart data={agentData} layout="vertical" barSize={18}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--c-border)" />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                            <YAxis type="category" dataKey="name" width={catAxisW} tick={{ fontSize: narrow ? 10 : 11, fill: 'var(--c-text-2)' }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(139, 92, 246, .08)' }} />
                            <Bar dataKey="count" name="Tickets" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
                        </BarChart>
                    </ChartCard>
                </div>
            )}

            {(stats.csatByAgent || []).length > 0 && (
                <div className="dash-charts-row full">
                    <ChartCard title="CSAT promedio por agente" icon={<span style={{ color: '#f59e0b' }}>{I.star}</span>} height={Math.max(180, stats.csatByAgent.length * 36)}>
                        <BarChart data={stats.csatByAgent} layout="vertical" barSize={18}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--c-border)" />
                            <XAxis type="number" domain={[0, 5]} allowDecimals tick={{ fontSize: 11, fill: 'var(--c-text-3)' }} axisLine={{ stroke: 'var(--c-border)' }} tickLine={false} />
                            <YAxis type="category" dataKey="name" width={catAxisW} tick={{ fontSize: narrow ? 10 : 11, fill: 'var(--c-text-2)' }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} ★`, 'CSAT']} cursor={{ fill: 'rgba(245, 158, 11, .08)' }} />
                            <Bar dataKey="avg" name="CSAT" fill="#f59e0b" radius={[0, 6, 6, 0]} />
                        </BarChart>
                    </ChartCard>
                </div>
            )}
        </>
    );
}

const tooltipStyle = {
    background: 'var(--c-card)',
    border: '1px solid var(--c-border)',
    borderRadius: 8,
    fontSize: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,.08)',
    color: 'var(--c-text-1)',
};


// ── Dashboard Admin ───────────────────────────────────────────────────────────
function AdminDashboard({ stats, loading, logout, pais, setPais }) {
    const count = (val) => stats?.byStatus?.find(s => s.status === val)?.count || 0;
    const paises = (stats?.byPais || []).map(p => p.pais);

    return (
        <div className="dash-root">
            <div className="dash-container dash-fade">

                <div className="dash-header">
                    <div>
                        <h1 className="dash-title">Panel de administración</h1>
                        <p className="dash-subtitle">Vista global del sistema</p>
                    </div>
                    <div className="dash-actions">
                        {paises.length > 0 && (
                            <select className="dash-select" value={pais} onChange={e => setPais(e.target.value)}>
                                <option value="all">🌐 Todos los países</option>
                                {paises.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        )}
                        <Link to="/tickets/new" className="dash-btn dash-btn-primary">{I.plus} Nuevo ticket</Link>
                        <Link to="/departments" className="dash-btn dash-btn-ghost">{I.building} Departamentos</Link>
                        <Link to="/invite" className="dash-btn dash-btn-ghost">{I.users} Invitar agente</Link>
                        <button onClick={logout} className="dash-btn dash-btn-danger-ghost">{I.logout} Salir</button>
                    </div>
                </div>

                {loading ? (
                    <div className="dash-loading"><Spinner animation="border" variant="primary" /></div>
                ) : !stats ? null : (
                    <>
                        <div className="dash-kpi-grid">
                            <KpiCard title="Total"            value={stats.total}                              color="#6b7280" accentBg="#f3f4f6" icon={I.layers} />
                            <KpiCard title="Abiertos"         value={count('open')}                            color="#2563eb" accentBg="#dbeafe" icon={I.inbox} />
                            <KpiCard title="En progreso"     value={count('in_progress')}                     color="#d97706" accentBg="#fef3c7" icon={I.inProgress} />
                            <KpiCard title="Resueltos"         value={count('closed')}                          color="#059669" accentBg="#d1fae5" icon={I.check} />
                            <KpiCard title="Vencidos"         value={stats.overdue}                            color="#dc2626" accentBg="#fee2e2" icon={I.alert} />
                            <KpiCard title="Resolución prom." value={fmtHours(stats.avgResolutionHours)}       color="#8b5cf6" accentBg="#ede9fe" icon={I.clock} subtitle="tiempo promedio" />
                            <KpiCard
                                title="CSAT promedio"
                                value={fmtCsat(stats.avgCsatRating)}
                                color="#f59e0b" accentBg="#fef3c7" icon={I.star}
                                subtitle={`${stats.csatCount ?? 0} resp. · ${stats.csatResponseRate ?? 0}% tasa`}
                            />
                        </div>

                        <StatsCharts stats={stats} />

                        <div className="dash-charts-row full" style={{ marginTop: 4 }}>
                            <DashboardCalendar />
                        </div>

                        <p className="dash-footer-meta">
                            <span className="dash-pulse" /> Actualizado: {new Date(stats.generatedAt).toLocaleString()}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}


// ── Dashboard Agente / Líder ─────────────────────────────────────────────────
function AgentDashboard({ stats, loading, pais, setPais }) {
    const count = (val) => stats?.byStatus?.find(s => s.status === val)?.count || 0;
    const paises = (stats?.byPais || []).map(p => p.pais);

    return (
        <div className="dash-root">
            <div className="dash-container dash-fade">

                <div className="dash-header">
                    <div>
                        <h1 className="dash-title">Panel del agente</h1>
                        <p className="dash-subtitle">Tickets en tus departamentos</p>
                    </div>
                    <div className="dash-actions">
                        {paises.length > 0 && (
                            <select className="dash-select" value={pais} onChange={e => setPais(e.target.value)}>
                                <option value="all">🌐 Todos los países</option>
                                {paises.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        )}
                        <Link to="/tickets" className="dash-btn dash-btn-primary">{I.inbox} Ver tickets</Link>
                        <Link to="/tickets/new" className="dash-btn dash-btn-ghost">{I.plus} Nuevo ticket</Link>
                    </div>
                </div>

                {loading ? (
                    <div className="dash-loading"><Spinner animation="border" variant="primary" /></div>
                ) : !stats ? null : (
                    <>
                        <div className="dash-kpi-grid">
                            <KpiCard title="Total"            value={stats.total}                              color="#6b7280" accentBg="#f3f4f6" icon={I.layers} />
                            <KpiCard title="Abiertos"         value={count('open')}                            color="#2563eb" accentBg="#dbeafe" icon={I.inbox} />
                            <KpiCard title="En progreso"     value={count('in_progress')}                     color="#d97706" accentBg="#fef3c7" icon={I.inProgress} />
                            <KpiCard title="Vencidos"         value={stats.overdue}                            color="#dc2626" accentBg="#fee2e2" icon={I.alert} />
                            <KpiCard title="Resolución prom." value={fmtHours(stats.avgResolutionHours)}       color="#8b5cf6" accentBg="#ede9fe" icon={I.clock} subtitle="tiempo promedio" />
                            <KpiCard title="Mi CSAT"          value={fmtCsat(stats.avgCsatRating)}             color="#f59e0b" accentBg="#fef3c7" icon={I.star} />
                        </div>

                        <StatsCharts stats={stats} />

                        <div className="dash-charts-row full" style={{ marginTop: 4 }}>
                            <DashboardCalendar />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}


// ── Dashboard Usuario ────────────────────────────────────────────────────────
function UserDashboard({ stats, loading }) {
    const count   = (val) => stats?.byStatus?.find(s => s.status === val)?.count || 0;
    const pieData = buildStatusData(stats?.byStatus || []).filter(d => d.count > 0);

    return (
        <div className="dash-root">
            <div className="dash-container-narrow dash-fade">

                <div className="dash-header">
                    <div>
                        <h1 className="dash-title">Mesa de ayuda</h1>
                        <p className="dash-subtitle">Crea y consulta el estado de tus solicitudes.</p>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
                    <Link to="/tickets/new" className="dash-btn dash-btn-primary" style={{ padding: '10px 20px', fontSize: 14 }}>{I.plus} Crear nuevo ticket</Link>
                    <Link to="/tickets" className="dash-btn dash-btn-ghost" style={{ padding: '10px 20px', fontSize: 14 }}>{I.inbox} Ver mis tickets</Link>
                </div>

                {stats?.overdue > 0 && (
                    <div className="dash-alert">
                        {I.alert}
                        <span>
                            Tienes <strong>{stats.overdue}</strong> ticket{stats.overdue > 1 ? 's' : ''} vencido{stats.overdue > 1 ? 's' : ''}.{' '}
                            <Link to="/tickets">Revisar</Link>
                        </span>
                    </div>
                )}

                {loading ? (
                    <div className="dash-loading"><Spinner animation="border" variant="primary" /></div>
                ) : !stats ? null : (
                    <>
                        <div className="dash-kpi-grid cols-3">
                            <KpiCard title="Total"    value={stats.total}     color="#6b7280" accentBg="#f3f4f6" icon={I.layers} />
                            <KpiCard title="Abiertos" value={count('open')}   color="#2563eb" accentBg="#dbeafe" icon={I.inbox} />
                            <KpiCard title="Resueltos" value={count('closed')} color="#059669" accentBg="#d1fae5" icon={I.check} />
                        </div>

                        {pieData.length > 0 && (
                            <ChartCard title="Mis tickets por estado" height={260}>
                                <PieChart>
                                    <Pie data={pieData} dataKey="count" nameKey="name"
                                        innerRadius={65} outerRadius={100} paddingAngle={3}
                                        stroke="var(--c-card)" strokeWidth={2}
                                        label={({ name, count: v }) => `${name}: ${v}`}>
                                        {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                    </Pie>
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                </PieChart>
                            </ChartCard>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}


// ── Componente principal ─────────────────────────────────────────────────────
export default function Dashboard() {
    const { user, logout } = useContext(AuthContext);
    const [stats,   setStats]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [pais,    setPais]    = useState('all');

    // loadStats puede llamarse "silent": no toca setLoading (sin spinner) y no
    // cambia stats hasta tener data nueva. Útil para auto-refresh por socket.
    const loadStats = async (paisFilter, { silent = false } = {}) => {
        try {
            const params = {};
            if (paisFilter && paisFilter !== 'all') params.pais = paisFilter;
            const res = await api.get('/tickets/stats', { params });
            setStats(res.data);
        } catch (e) {
            console.error('No se pudieron cargar las estadísticas', e);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        loadStats(pais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pais]);

    // Auto-refresh por eventos de socket — DEBOUNCED 5s y SILENT (sin spinner).
    // Con 1500 clientes y ~8 eventos/min en hora pico, sin debounce se generarían
    // miles de requests/min al backend. Ahora colapsamos en 1 refetch silenciosa.
    useEffect(() => {
        let timer = null;
        const handler = () => {
            clearTimeout(timer);
            timer = setTimeout(() => loadStats(pais, { silent: true }), 5000);
        };
        const events = ['ticketCreated', 'ticketUpdated', 'ticketEscalated', 'ticketOverdue'];
        events.forEach(ev => socket.on(ev, handler));
        return () => {
            events.forEach(ev => socket.off(ev, handler));
            clearTimeout(timer);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pais]);

    return (
        <>
            <style>{DASH_CSS}</style>
            {user?.role === 'admin'                                   ? <AdminDashboard stats={stats} loading={loading} logout={logout} pais={pais} setPais={setPais} />
             : (user?.role === 'agent' || user?.role === 'leader')    ? <AgentDashboard stats={stats} loading={loading} pais={pais} setPais={setPais} />
             :                                                          <UserDashboard  stats={stats} loading={loading} />}
        </>
    );
}
